import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button, Menu, Text } from '@fragments';
import { IconAlertTriangle, IconRefresh, IconX } from '@tabler/icons-react';
import { ATTACHMENT_ROLES } from '../../../../enums/attachments';
import './styles.scss';

// The images attached to the prompt, as a row above the field (useAttachments). Each one
// previews from the local file while it uploads; tapping it picks what the AI should do
// with it (a post reference, the art's background, a photo or the logo, or its own call).
// A failed upload is tapped to retry. The send button waits until every one is uploaded.
export default function ComposerAttachments({ studio: s }) {
  const { t, attachments: a } = s;
  const reduced = useReducedMotion();
  if (!a?.items.length && !a?.notice) return null;
  const roles = item => ATTACHMENT_ROLES.map(role => ({ id: role, label: t(`attachRole_${role}`), active: item.role === role, onClick: () => a.setRole(item.id, role) }));
  const motionProps = { initial: { opacity: 0, scale: reduced ? 1 : 0.9 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: reduced ? 1 : 0.9 }, transition: { duration: reduced ? 0 : 0.16 } };
  return <div className="studio-attachments">
    {a.items.length > 0 && <div className="studio-attachments__row" role="group" aria-label={t('attachments')} aria-busy={a.uploading || undefined}>
      <AnimatePresence initial={false}>
        {a.items.map(item => {
          const role = t(`attachRole_${item.role}`);
          const thumb = <img src={item.asset?.url || item.preview} alt="" draggable={false} />;
          return <motion.div key={item.id} layout={!reduced} className={`studio-attachment is-${item.status}`} {...motionProps}>
            {item.status === 'failed'
              ? <button type="button" className="studio-attachment__thumb" title={t('attachFailed')} aria-label={`${item.name}: ${t('attachFailed')} · ${t('retry')}`} onClick={() => a.retry(item.id)}>
                {thumb}<span className="studio-attachment__state"><IconRefresh aria-hidden="true" /></span>
              </button>
              : <Menu items={roles(item)} placement="top-start" header={<Text.caption contrast={6}>{t('attachRole')}</Text.caption>}>
                <button type="button" className="studio-attachment__thumb" title={`${t('attachRole')}: ${role}`}
                  aria-label={`${item.name} · ${t('attachRole')}: ${role}${item.status === 'uploading' ? ` · ${t('attachUploading')}` : ''}`}>
                  {thumb}
                  {item.status === 'uploading' && <span className="studio-attachment__state" aria-hidden="true"><span className="studio-attachment__spinner" /></span>}
                  {item.role !== 'auto' && <span className="studio-attachment__role">{t(`attachRoleShort_${item.role}`)}</span>}
                </button>
              </Menu>}
            <Button className="studio-attachment__remove" size="micro" variant="rounded" color="overlay" preventLoading icon={<IconX />}
              aria-label={`${t('attachRemove')}: ${item.name}`} tooltip={t('attachRemove')} onClick={() => a.remove(item.id)} />
          </motion.div>;
        })}
      </AnimatePresence>
      <Text.caption contrast={5} className="studio-attachments__count">{t('attachCount', { count: a.items.length, limit: a.limit })}</Text.caption>
    </div>}
    {a.uploading && <span className="studio-attachments__live" role="status">{t('attachUploading')}</span>}
    {a.notice && <Text.caption contrast={8} role="alert" className="studio-attachments__notice" left={<IconAlertTriangle />}>{t(a.notice.key, a.notice.params)}</Text.caption>}
  </div>;
}
